import { derived, type Readable } from 'svelte/store';
import { createQuery, type QueryClient } from '$lib/api/tanstack-query';

import { assertExists } from '$lib/assert';
import type { AuthContext } from '$lib/auth';

import type { FeatureFlagServiceClient } from './client';
import { getFeatureFlags } from './get-feature-flag';
import { useOrgsContext } from './orgs';
import { prefetchQuery } from './prefetch';
import {
  type BaseQueryOptions,
  createQueryOptions,
  getAuthedQueryOptions,
  useAuthedQueryOptions,
} from './query-options';

/**
 * Per-org feature flags returned by the FeatureFlagService.
 *
 * **IMPORTANT**: Adding a flag here is NOT enough — you must ALSO register it in the Go backend
 * at `data/feature_models.go`. Without backend registration, the flag will never be returned by
 * GetFeatures and will always be undefined (falsy) at runtime.
 *
 * See `.claude/rules/feature-flags.md` for the full checklist.
 */
export interface FeatureFlags {
  // TODO(mc, 2024-08-30): launch or remove pending new product spec
  // https://viam.atlassian.net/issues/APP-4691
  SHOW_DATA_CAPTURE_ICON?: boolean;

  // TODO(KS, 2024-09-17): Remove on launch of slam page
  // https://viam.atlassian.net/browse/APP-6272
  SHOW_FLEET_SLAM_MAPS?: boolean;

  // TODO(APP-7189): Remove on launch of device settings card
  SHOW_DEVICE_SETTINGS_CARD?: boolean;

  // TODO: Remove on launch of new sensors tab
  SHOW_NEW_SENSORS_TAB?: boolean;

  // TODO(APP-8235): Remove on launch of error count resource threshold
  ERROR_COUNT_RESOURCE_THRESHOLD?: number;

  // TODO(APP-11084): Remove on launch of query assistant
  ENABLE_QUERY_ASSISTANT?: boolean;

  ERROR_BANNER?: string;

  // JSON string for a dismissable system-wide announcement banner. Shape:
  //   { startTime: <unix seconds>, badge?: <pill text>,
  //     segments: [{ text, style?: "strong" | "subtle", href? }] }
  // Each segment is an inline run; add `href` to make it a link, `style` to emphasize.
  // Example: {"startTime":1719600000,"badge":"New","segments":[
  //   {"text":"VIAM 101","style":"strong"},{"text":"register","href":"https://app.viam.com"}]}
  // See $lib/announcement-banner.
  ANNOUNCEMENT_BANNER?: string;

  ENABLE_PIPELINE_UI?: boolean;

  ENABLE_SEQUENCES_UI?: boolean;

  SHOW_HYPERPARAMETER_TUNING?: boolean;

  ENABLE_EDIT_FRAME_IN_VIZ_TAB?: boolean;

  ENABLE_MODEL_METRICS?: boolean;

  USE_HTML_LABELER?: boolean;

  USE_LABELER_ZOOM?: boolean;

  RENDER_3D_ARM_MODELS_VIZ?: boolean;

  SHOW_DEFAULT_FRAGMENTS?: boolean;

  ENABLE_PUSH_NOTIFICATIONS?: boolean;

  ENABLE_ASK_AI_BUTTON?: boolean;

  ENABLE_LLM_SCENE_BUILDER?: boolean;

  ENABLE_MOTION_PLAN_REPLAYER?: boolean;

  ENABLE_MODEL_EVALUATION?: boolean;

  ENABLE_CUSTOM_TRAINING_CONTAINERS?: boolean;

  // Selects the config AI assistant model: "sonnet" | "opus".
  CONFIG_AI_ASSISTANT_MODEL?: string;

  // Overrides the config AI assistant system prompt; empty uses the built-in default. Read server-side only.
  CONFIG_AI_ASSISTANT_SYSTEM_PROMPT?: string;

  /**
   * Token prices for the internal AI-assistant stats dashboard, USD per 1M tokens, keyed by model.
   * JSON object: `{"<model>": {label, input, output}}`; models left out keep their built-in price.
   * Empty uses the built-in table. See $lib/config-assistant-pricing.
   */
  CONFIG_AI_ASSISTANT_PRICING?: string;

  // TODO: Remove on launch of assisted setup UI
  ENABLE_ASSISTED_SETUP_UI?: boolean;

  // TODO: Remove on launch of machine simulation
  ENABLE_SIMULATION_UI?: boolean;

  // TODO: Remove on launch of machine favorites
  ENABLE_MACHINE_FAVORITES_UI?: boolean;
}

const base = ['features'];
export const featureQueryKeys = {
  features: (orgID: string) => [...base, orgID] as const,
};

const createGetFeatureQuery = () => {
  const baseOptions = useAuthedQueryOptions('feature');
  const { currentOrgID } = useOrgsContext();

  const options = derived(
    [baseOptions, currentOrgID],
    ([$baseOptions, $currentOrgID]) =>
      featureFlagsQueryOptions($baseOptions, { orgID: $currentOrgID })
  );

  return createQuery(options);
};

export const useFeatureFlags = (): Readable<FeatureFlags> => {
  const featureFlagsQuery = createGetFeatureQuery();
  return derived(featureFlagsQuery, ({ data }) =>
    getFeatureFlags(data?.features)
  );
};

// Prefetch `featureFlags`
export interface CreateFeatureFlagQueryRequest {
  orgID: string | undefined;
}

export const prefetchFeatureFlags = async (
  queryClient: QueryClient,
  client: FeatureFlagServiceClient,
  authContext: AuthContext,
  request: CreateFeatureFlagQueryRequest
) => {
  const baseAuthedOptions = getAuthedQueryOptions(client, authContext);
  const options = featureFlagsQueryOptions(baseAuthedOptions, request);

  return prefetchQuery(queryClient, options);
};

const featureFlagsQueryOptions = (
  {
    client,
    getRPCOptions,
    enabled,
  }: BaseQueryOptions<FeatureFlagServiceClient>,
  request: CreateFeatureFlagQueryRequest
) => {
  return createQueryOptions({
    queryKey: featureQueryKeys.features(request.orgID ?? ''),
    enabled: enabled && request.orgID !== undefined,
    queryFn: async () => {
      assertExists(request.orgID, 'Missing org ID');
      const rpcOptions = await getRPCOptions();
      const { response } = await client.getFeatures(
        { organizationId: request.orgID },
        rpcOptions
      );
      return response;
    },
  });
};
